<template>
  <div class="n8n-page">
    <!-- Header -->
    <header class="n8n-header">
      <div class="n8n-header__brand">
        <img src="../assets/images/logo2.png" class="n8n-header__logo" loading="lazy" alt="Logo" />
        <div class="n8n-header__info">
          <span class="n8n-header__title">UFA Easy888</span>
          <span class="n8n-header__badge">{{ code }}</span>
        </div>
      </div>
      <div class="n8n-header__avatar" :style="{ backgroundImage: `url(${pictureUrl})` }"></div>
    </header>

    <!-- Main Content -->
    <main class="n8n-main">
      <!-- Page Hero -->
      <div class="n8n-hero">
        <div class="n8n-hero__icon">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"/>
            <polyline points="12,6 12,12 16,14"/>
          </svg>
        </div>
        <h1 class="n8n-hero__title">ประวัติธุรกรรม</h1>
        <p class="n8n-hero__subtitle">รายการฝาก-ถอนทั้งหมดของคุณ</p>
      </div>

      <!-- Stats Cards -->
      <div class="n8n-stats">
        <div class="n8n-stat n8n-stat--deposit">
          <div class="n8n-stat__icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="23,6 13.5,15.5 8.5,10.5 1,18"/>
              <polyline points="17,6 23,6 23,12"/>
            </svg>
          </div>
          <div class="n8n-stat__content">
            <span class="n8n-stat__label">ฝากทั้งหมด</span>
            <span class="n8n-stat__value">{{ totalDeposit }}</span>
          </div>
        </div>
        <div class="n8n-stat n8n-stat--withdraw">
          <div class="n8n-stat__icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="23,18 13.5,8.5 8.5,13.5 1,6"/>
              <polyline points="17,18 23,18 23,12"/>
            </svg>
          </div>
          <div class="n8n-stat__content">
            <span class="n8n-stat__label">ถอนทั้งหมด</span>
            <span class="n8n-stat__value">{{ totalWithdraw }}</span>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div class="n8n-loading" v-if="loading">
        <span class="n8n-spinner n8n-spinner--lg"></span>
        <span>กำลังโหลดข้อมูล...</span>
      </div>

      <!-- History List -->
      <div class="n8n-list" v-else-if="history.length > 0">
        <div class="n8n-list__header">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
            <polyline points="14,2 14,8 20,8"/>
            <line x1="16" y1="13" x2="8" y2="13"/>
            <line x1="16" y1="17" x2="8" y2="17"/>
            <polyline points="10,9 9,9 8,9"/>
          </svg>
          <span>รายการทั้งหมด</span>
          <span class="n8n-list__count">{{ history.length }} รายการ</span>
        </div>

        <div class="n8n-list__items">
          <div 
            v-for="(item, index) in history" 
            :key="index" 
            class="n8n-item"
            :class="item.type === 'ฝาก' ? 'n8n-item--deposit' : 'n8n-item--withdraw'"
          >
            <div class="n8n-item__icon">
              <svg v-if="item.type === 'ฝาก'" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="16"/>
                <line x1="8" y1="12" x2="16" y2="12"/>
              </svg>
              <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="8" y1="12" x2="16" y2="12"/>
              </svg>
            </div>
            
            <div class="n8n-item__content">
              <div class="n8n-item__type">{{ item.type }}</div>
              <div class="n8n-item__date">{{ item.createdateShow }}</div>
            </div>
            
            <div class="n8n-item__amount">
              {{ item.type === 'ฝาก' ? '+' : '-' }}{{ formatNumber(item.qty) }}
            </div>
            
            <div class="n8n-item__status" :class="getStatusClass(item.status)">
              <svg v-if="getStatusIcon(item.status) === 'check'" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                <polyline points="20,6 9,17 4,12"/>
              </svg>
              <svg v-else-if="getStatusIcon(item.status) === 'clock'" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10"/>
                <polyline points="12,6 12,12 16,14"/>
              </svg>
              <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="15" y1="9" x2="9" y2="15"/>
                <line x1="9" y1="9" x2="15" y2="15"/>
              </svg>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div class="n8n-empty" v-else>
        <div class="n8n-empty__icon">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <rect x="2" y="4" width="20" height="16" rx="2"/>
            <path d="M2 10h20"/>
            <path d="M12 14v2"/>
          </svg>
        </div>
        <h3 class="n8n-empty__title">ไม่มีประวัติธุรกรรม</h3>
        <p class="n8n-empty__text">คุณยังไม่มีรายการฝาก-ถอน</p>
      </div>
    </main>

    <!-- Bottom Menu -->
    <BottomMenu></BottomMenu>
  </div>
</template>

<script>
import firebase from 'firebase/app';
import 'firebase/firestore';
import 'firebase/database';
import moment from "moment-timezone";
import router from "./../router";
import liffService from "@/services/liff";
export default {
  name: 'History',
  components: {
    BottomMenu: require("./BottomMenu").default,
  },
  created() {
    // LIFF is already initialized by router guard
    this.loadUserData();
  },
  data() {
    return {
      menuVisible: false,
      pictureUrl:'',
      history:[],
      code:"",
      loading: false,
      totalDeposit: '0',
      totalWithdraw: '0',
    }
  },
  computed: {
    calculateTotals() {
      let deposit = 0;
      let withdraw = 0;
      this.history.forEach(item => {
        const amount = parseFloat(item.qty.toString().replace(/,/g, ''));
        if (item.type === 'ฝาก') {
          deposit += amount;
        } else {
          withdraw += amount;
        }
      });
      return { deposit, withdraw };
    }
  },
  methods: {
    async loadUserData() {
      const isLocalhost = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
      
      // Get profile from centralized service (already fetched by router guard)
      let profile = liffService.profile;
      
      // Mock LIFF profile for localhost development
      if (isLocalhost && !profile) {
        profile = {
          userId: "UmockUserIdForDev",
          displayName: "Dev Tester",
          pictureUrl: "",
          statusMessage: ""
        };
      }
      
      if (!profile) return;

      this.userId = profile.userId || "";
      this.pictureUrl = profile.pictureUrl || "none";
      
      if (this.userId) {
        // Development mode on localhost - show mock history
        if (isLocalhost) {
          this.code = "DEV001";
          this.history = [
            { key: '1', type: 'ฝาก', qty: '1000', status: 'สำเร็จ', createdateShow: '08 Jan,2026' },
            { key: '2', type: 'ถอน', qty: '500', status: 'สำเร็จ', createdateShow: '07 Jan,2026' },
            { key: '3', type: 'ฝาก', qty: '2000', status: 'กำลังดำเนินการ', createdateShow: '06 Jan,2026' },
            { key: '4', type: 'ถอน', qty: '800', status: 'สำเร็จ', createdateShow: '05 Jan,2026' },
          ];
          this.totalDeposit = '3,000';
          this.totalWithdraw = '1,300';
          return;
        }
        
        this.getDatatHistory(this.userId);
        const users = (
          await firebase
            .database()
            .ref(`/users`)
            .orderByChild("userId")
            .equalTo(this.userId)
            .once("value")
        ).val();
        if (!users) {
          router.replace("/register");
        } else {
          let user = {};
          for (const x in users) {
            user = users[x];
          }
          this.code = user.code;
        }
      }
    },
    getDatatHistory(userId){
      this.loading = true;
      firebase.firestore().collection('history').where('userId', '==', userId).orderBy('createdate','desc').get().then((querySnapshot) => {
          const history = [];
          let totalDeposit = 0;
          let totalWithdraw = 0;
          
          querySnapshot.forEach(function (doc) {
              const data = doc.data();
              data.key = doc.id;
              data.createdateShow = moment(data.createdate,'YYYY-MM-DD HH:mm:ss').format('DD MMM,YYYY');
              history.push(data);
              
              const amount = parseFloat(data.qty.toString().replace(/,/g, ''));
              if (data.type === 'ฝาก') {
                totalDeposit += amount;
              } else if (data.type === 'ถอน') {
                totalWithdraw += amount;
              }
          });
          
          this.history = history;
          this.totalDeposit = this.formatNumber(totalDeposit);
          this.totalWithdraw = this.formatNumber(totalWithdraw);
          this.loading = false;
      }).catch(() => {
          this.loading = false;
      });
    },
    formatNumber(num) {
      return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    },
    getStatusClass(status) {
      if (status === 'ดำเนินการแล้ว' || status === 'สำเร็จ') {
        return 'n8n-item__status--success';
      } else if (status === 'กำลังดำเนินการ') {
        return 'n8n-item__status--pending';
      } else {
        return 'n8n-item__status--failed';
      }
    },
    getStatusIcon(status) {
      if (status === 'ดำเนินการแล้ว' || status === 'สำเร็จ') {
        return 'check';
      } else if (status === 'กำลังดำเนินการ') {
        return 'clock';
      } else {
        return 'x';
      }
    }
  }
}
</script>

<style lang="scss" scoped>
// ============================================================================
// N8N THEME - HISTORY PAGE
// ============================================================================

// Design Tokens
$n8n-bg: #0a0a0a;
$n8n-surface: #151515;
$n8n-surface-hover: #1f1f1f;
$n8n-border: rgba(255, 255, 255, 0.08);
$n8n-primary: #FF6B35;
$n8n-primary-soft: rgba(255, 107, 53, 0.15);
$n8n-success: #10b981;
$n8n-success-soft: rgba(16, 185, 129, 0.15);
$n8n-danger: #ef4444;
$n8n-danger-soft: rgba(239, 68, 68, 0.15);
$n8n-warning: #f59e0b;
$n8n-warning-soft: rgba(245, 158, 11, 0.15);
$n8n-text: #ffffff;
$n8n-text-secondary: rgba(255, 255, 255, 0.6);
$n8n-text-muted: rgba(255, 255, 255, 0.4);
$n8n-radius: 12px;
$n8n-radius-sm: 8px;

.n8n-page {
  min-height: 100vh;
  background: transparent;
  color: $n8n-text;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  padding-bottom: 100px;
}

// Header
.n8n-header {
  position: sticky;
  top: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  padding-top: calc(1rem + env(safe-area-inset-top, 0px));
  background: rgba(10, 10, 10, 0.85);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-bottom: 1px solid $n8n-border;

  &__brand {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  &__logo {
    height: 44px;
    width: auto;
  }

  &__info {
    display: flex;
    flex-direction: column;
  }

  &__title {
    font-size: 1rem;
    font-weight: 600;
    color: $n8n-text;
  }

  &__badge {
    font-size: 0.75rem;
    color: $n8n-primary;
    font-weight: 500;
  }

  &__avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background-size: cover;
    background-position: center;
    background-color: $n8n-surface;
    border: 2px solid $n8n-border;
  }
}

// Main Content
.n8n-main {
  padding: 1.5rem 1rem;
  max-width: 480px;
  margin: 0 auto;
}

// Hero Section
.n8n-hero {
  text-align: center;
  margin-bottom: 1.5rem;
  
  &__icon {
    width: 64px;
    height: 64px;
    margin: 0 auto 1rem;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%);
    border-radius: 16px;
    color: white;
    box-shadow: 0 8px 32px rgba(124, 58, 237, 0.3);
  }

  &__title {
    font-size: 1.75rem;
    font-weight: 700;
    margin: 0 0 0.5rem;
    background: linear-gradient(135deg, #fff 0%, rgba(255,255,255,0.8) 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  &__subtitle {
    font-size: 0.95rem;
    color: $n8n-text-secondary;
    margin: 0;
  }
}

// Stats
.n8n-stats {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
}

.n8n-stat {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  background: $n8n-surface;
  border: 1px solid $n8n-border;
  border-radius: $n8n-radius;

  &--deposit {
    .n8n-stat__icon {
      background: $n8n-success-soft;
      color: $n8n-success;
    }
    .n8n-stat__value {
      color: $n8n-success;
    }
  }

  &--withdraw {
    .n8n-stat__icon {
      background: $n8n-danger-soft;
      color: $n8n-danger;
    }
    .n8n-stat__value {
      color: $n8n-danger;
    }
  }

  &__icon {
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: $n8n-radius-sm;
    flex-shrink: 0;
  }

  &__content {
    flex: 1;
    min-width: 0;
  }

  &__label {
    display: block;
    font-size: 0.75rem;
    color: $n8n-text-muted;
    margin-bottom: 0.125rem;
  }

  &__value {
    font-size: 1.125rem;
    font-weight: 700;
  }
}

// Loading
.n8n-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  gap: 1rem;
  color: $n8n-text-muted;
}

// Spinner
.n8n-spinner {
  width: 20px;
  height: 20px;
  border: 2px solid $n8n-border;
  border-top-color: $n8n-primary;
  border-radius: 50%;
  animation: n8n-spin 0.8s linear infinite;
  
  &--lg {
    width: 32px;
    height: 32px;
    border-width: 3px;
  }
}

@keyframes n8n-spin {
  to { transform: rotate(360deg); }
}

// List
.n8n-list {
  &__header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.875rem 1rem;
    background: $n8n-surface;
    border: 1px solid $n8n-border;
    border-radius: $n8n-radius $n8n-radius 0 0;
    font-weight: 600;
    font-size: 0.9rem;
    color: $n8n-text;
    
    svg {
      color: $n8n-primary;
    }
  }

  &__count {
    margin-left: auto;
    font-size: 0.75rem;
    color: $n8n-text-muted;
    font-weight: 500;
  }

  &__items {
    border: 1px solid $n8n-border;
    border-top: none;
    border-radius: 0 0 $n8n-radius $n8n-radius;
    overflow: hidden;
  }
}

// Item
.n8n-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  background: $n8n-surface;
  border-bottom: 1px solid $n8n-border;
  transition: background 0.2s ease;
  
  &:last-child {
    border-bottom: none;
  }

  &:active {
    background: $n8n-surface-hover;
  }

  &--deposit {
    .n8n-item__icon {
      background: $n8n-success-soft;
      color: $n8n-success;
    }
    .n8n-item__amount {
      color: $n8n-success;
    }
  }

  &--withdraw {
    .n8n-item__icon {
      background: $n8n-danger-soft;
      color: $n8n-danger;
    }
    .n8n-item__amount {
      color: $n8n-danger;
    }
  }

  &__icon {
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    flex-shrink: 0;
  }

  &__content {
    flex: 1;
    min-width: 0;
  }

  &__type {
    font-size: 0.9rem;
    font-weight: 600;
    color: $n8n-text;
    margin-bottom: 0.125rem;
  }

  &__date {
    font-size: 0.75rem;
    color: $n8n-text-muted;
  }

  &__amount {
    font-size: 1rem;
    font-weight: 700;
    font-family: 'SF Mono', 'Consolas', monospace;
  }

  &__status {
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    flex-shrink: 0;

    &--success {
      background: $n8n-success-soft;
      color: $n8n-success;
    }

    &--pending {
      background: $n8n-warning-soft;
      color: $n8n-warning;
    }

    &--failed {
      background: $n8n-danger-soft;
      color: $n8n-danger;
    }
  }
}

// Empty State
.n8n-empty {
  text-align: center;
  padding: 3rem 2rem;
  background: $n8n-surface;
  border: 1px solid $n8n-border;
  border-radius: $n8n-radius;

  &__icon {
    margin-bottom: 1rem;
    color: $n8n-text-muted;
  }

  &__title {
    font-size: 1.125rem;
    font-weight: 600;
    color: $n8n-text;
    margin: 0 0 0.5rem;
  }

  &__text {
    font-size: 0.9rem;
    color: $n8n-text-muted;
    margin: 0;
  }
}
</style>
